import { AuthUser } from '../../models/user';

// The form of XD screen 9 ("9. Add user" in DESIGN.md), which
// also serves to modify (PATCH). The switch keys are
// the columns of `users`; "Pour le compte de" is the `users` array.

export type UserRole = 'ADMIN' | 'USER';

export type UserFormValues = {
  firstname: string;
  lastname: string;
  email: string;
  function: string;
  role: '' | UserRole;
  services: string[];
  can_issue_document: boolean;
  can_issue_unsecure_document: boolean;
  can_archive_secure_document: boolean;
  users: string[];
};

export type UserFormErrors = Partial<
  Record<'firstname' | 'lastname' | 'email' | 'role' | 'services', string>
>;

export const EMPTY_USER_FORM: UserFormValues = {
  firstname: '',
  lastname: '',
  email: '',
  function: '',
  role: '',
  services: [],
  can_issue_document: false,
  can_issue_unsecure_document: false,
  can_archive_secure_document: false,
  users: [],
};

export const formValuesFromUser = (user: AuthUser): UserFormValues => ({
  firstname: user.firstname ?? '',
  lastname: user.lastname ?? '',
  email: user.email ?? '',
  function: user.function ?? '',
  role: user.role === 'ADMIN' || user.role === 'USER' ? user.role : '',
  services: user.services ?? [],
  can_issue_document: Boolean(user.can_issue_document),
  can_issue_unsecure_document: Boolean(user.can_issue_unsecure_document),
  can_archive_secure_document: Boolean(user.can_archive_secure_document),
  users: user.users ?? [],
});

// What `POST /api/users/` and `PATCH /api/users/` expect (the latter with
// `id`). The field names are the model's.
export const toPayload = (values: UserFormValues, id?: string) => ({
  ...(id ? { id } : {}),
  firstname: values.firstname.trim(),
  lastname: values.lastname.trim(),
  email: values.email.trim(),
  function: values.function.trim(),
  role: values.role,
  services: values.services,
  can_issue_document: values.can_issue_document,
  can_issue_unsecure_document: values.can_issue_unsecure_document,
  can_archive_secure_document: values.can_archive_secure_document,
  users: values.users,
});

// The same expression the previous form used.
const EMAIL_PATTERN = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

export const REQUIRED_MESSAGE = 'Ce champ est obligatoire.';
export const EMAIL_MESSAGE = "L'adresse email n'est pas valide";
export const SERVICE_MESSAGE = 'Sélectionnez au moins un service.';

// Client-side validation (the XD does not draw messages, gap 8): required
// Prénom, Nom, Email, Rôle and at least one Service, and the email format.
// It is what the backend needs to create the account and send the password
// email; it refuses an account without a service.
export const validateUserForm = (values: UserFormValues): UserFormErrors => {
  const errors: UserFormErrors = {};
  if (!values.firstname.trim()) errors.firstname = REQUIRED_MESSAGE;
  if (!values.lastname.trim()) errors.lastname = REQUIRED_MESSAGE;
  if (!values.email.trim()) {
    errors.email = REQUIRED_MESSAGE;
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = EMAIL_MESSAGE;
  }
  if (!values.role) errors.role = REQUIRED_MESSAGE;
  if (values.services.length === 0) errors.services = SERVICE_MESSAGE;
  return errors;
};
